import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TerminalKindDb } from './db';

/**
 * Device-side state that must survive reloads on an enrolled tablet: the terminal identity we
 * learned at enrollment and the current staff PIN session token. The auth session itself is kept
 * by supabase-js. Nothing here is trusted by the server; it re-validates on every call.
 */
export interface TerminalInfo {
  id: string;
  name: string;
  code: string;
  kind: TerminalKindDb;
  location_id: string;
  landing: string;
}

interface DeviceState {
  terminal: TerminalInfo | null;
  sessionToken: string | null;
  sessionEmployee: { id: string; display_name: string } | null;
  sessionExpiresAt: string | null;
  setTerminal: (t: TerminalInfo | null) => void;
  setSession: (
    token: string | null,
    employee: { id: string; display_name: string } | null,
    expiresAt: string | null,
  ) => void;
  clear: () => void;
}

export const useDeviceStore = create<DeviceState>()(
  persist(
    (set) => ({
      terminal: null,
      sessionToken: null,
      sessionEmployee: null,
      sessionExpiresAt: null,
      setTerminal: (terminal) => set({ terminal }),
      setSession: (sessionToken, sessionEmployee, sessionExpiresAt) =>
        set({ sessionToken, sessionEmployee, sessionExpiresAt }),
      clear: () =>
        set({ terminal: null, sessionToken: null, sessionEmployee: null, sessionExpiresAt: null }),
    }),
    { name: 'didto-os-device' },
  ),
);
